import { useState, useEffect } from 'react';
import {
  Button, TextField, Dialog, DialogActions, DialogContent,
  DialogTitle, CircularProgress, Alert,
} from '@mui/material';
import { showSuccessMessage } from '@/components/common/GlobalSnackbar.tsx';
import { apiClient } from '@/services/api';

// TypeScript 接口定义
interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id?: number;
  report_template_id?: number;
  client_level?: string;
  evaluation_type?: string;
  status: string;
  contract_year?: number;
  dify_dataset_id?: string;
  created_at: string;
  updated_at: string;
}

interface CreateMonitoringTargetDialogProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: () => void;
  editMode?: boolean;
  projectDataToEdit?: Project | null;
  projectType?: string; // 项目类型，默认为 'monitoring_target'，也可以是 'department_monitoring'
}

/**
 * 事中监控 - 具体目标监控：创建/编辑项目对话框
 * 与绩效目标申报表分离，使用独立的 project_type: "monitoring_target"
 */
export default function CreateMonitoringTargetDialog({
  open,
  onClose,
  onProjectCreated,
  editMode = false,
  projectDataToEdit = null,
  projectType = 'monitoring_target', // 默认为具体目标监控
}: CreateMonitoringTargetDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 配额检查相关状态（与其他创建对话框一致）
  const [checkingQuota, setCheckingQuota] = useState(false);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [canCreateProject, setCanCreateProject] = useState(true);

  // 表单字段：项目名称、年度、部门名称、单位名称
  const [projectName, setProjectName] = useState('');
  const [contractYear, setContractYear] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [unitName, setUnitName] = useState('');

  // 判断是否隐藏年度、部门、单位字段（目标、监控、自评类型不显示这些字段）
  const hideExtraFields = [
    'monitoring_target',
    'department_monitoring',
    'project_self_evaluation',
    'department_self_evaluation'
  ].includes(projectType);

  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
      setQuotaError(null);
      setCanCreateProject(true);

      // 创建模式下检查配额
      if (!editMode) {
        (async () => {
          setCheckingQuota(true);
          try {
            const userResp = await apiClient.get('/auth/session');
            if (userResp?.success && userResp.data?.id) {
              const userId = userResp.data.id;

              // 根据项目类型确定要检查的配额类型
              const quotaType = projectType === 'department_monitoring' ? 'department_monitoring' : 'monitoring_target';
              const quotaResp = await apiClient.get(`/auth/check-project-quota?userId=${userId}&projectType=${quotaType}`);

              if (!quotaResp?.success || !quotaResp.data?.canCreate) {
                setCanCreateProject(false);
                setQuotaError(quotaResp?.message || '无法创建项目，请先购买套餐');
              } else {
                setCanCreateProject(true);
                setQuotaError(null);
              }
            }
          } catch (err: any) {
            setQuotaError('检查配额失败，请稍后重试');
            setCanCreateProject(false);
          } finally {
            setCheckingQuota(false);
          }
        })();
      }

      if (editMode && projectDataToEdit) {
        // 编辑模式
        setProjectName(projectDataToEdit.project_name || '');
        setContractYear(
          projectDataToEdit.contract_year?.toString() ||
          new Date().getFullYear().toString(),
        );
        // 先尝试从项目对象中取（兼容以后可能直接挂在 Project 上的情况）
        setDepartmentName((projectDataToEdit as any).department_name || '');
        setUnitName((projectDataToEdit as any).unit_name || '');

        // 补充：从 performance-meta 中回显部门/单位名称
        (async () => {
          try {
            const metaResp = await apiClient.get(`/projects/${projectDataToEdit.id}/performance-meta`);
            if (metaResp?.success && metaResp.data) {
              if (!((projectDataToEdit as any).department_name)) {
                setDepartmentName(metaResp.data.department_name || '');
              }
              if (!((projectDataToEdit as any).unit_name)) {
                setUnitName(metaResp.data.unit_name || '');
              }
            }
          } catch {
            // 忽略回显错误，避免阻塞编辑
          }
        })();
      } else {
        // 创建模式
        setProjectName('');
        setContractYear(new Date().getFullYear().toString());
        setDepartmentName('');
        setUnitName('');
      }
    }
  }, [open, editMode, projectDataToEdit]);

  // 合同年度验证函数
  const isValidContractYear = (yearStr: string): boolean => {
    if (!yearStr || !yearStr.trim()) return true; // 可选字段

    const yearNum = parseInt(yearStr);
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 10;
    const maxYear = currentYear + 10;

    return yearNum >= minYear && yearNum <= maxYear;
  };

  const validateForm = (): boolean => {
    if (!projectName.trim()) {
      setError('项目名称是必填项。');
      return false;
    }

    // 只有在非隐藏模式下才验证年度、部门、单位字段
    if (!hideExtraFields) {
      if (!contractYear.trim()) {
        setError('年度为必填项。');
        return false;
      }
      if (!departmentName.trim()) {
        setError('部门名称为必填项。');
        return false;
      }
      if (!unitName.trim()) {
        setError('单位名称为必填项。');
        return false;
      }

      if (contractYear && contractYear.trim()) {
        const currentYear = new Date().getFullYear();
        const year = parseInt(contractYear);

        if (isNaN(year)) {
          setError('合同年度必须是有效的数字。');
          return false;
        }

        const minYear = currentYear - 10;
        const maxYear = currentYear + 10;

        if (year < minYear || year > maxYear) {
          setError(`合同年度必须在 ${minYear} 到 ${maxYear} 年之间。`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    // 创建时的默认值
    const creationDefaults = {
      status: 'new',
      industry_type: '',
      evaluation_type: '',
    };

    // 最终提交的数据：使用独立的 project_type
    // 根据是否隐藏额外字段，构建不同的 payload
    const projectPayload: Record<string, any> = {
      project_name: projectName,
      industry_type: '',
      project_type: projectType,
    };

    // 只有在非隐藏模式下才包含年度、部门、单位字段
    if (!hideExtraFields) {
      projectPayload.contract_year = contractYear.trim() || new Date().getFullYear().toString();
      projectPayload.department_name = departmentName;
      projectPayload.unit_name = unitName;
    }

    try {
      if (editMode && projectDataToEdit?.id) {
        // 编辑模式
        const response = await apiClient.put(`/projects/${projectDataToEdit.id}`, projectPayload);

        if (response.success) {
          showSuccessMessage('项目更新成功！');
        } else {
          throw new Error(response.message || '更新项目失败');
        }
      } else {
        // 创建模式
        const finalPayload = { ...projectPayload, ...creationDefaults };
        const response = await apiClient.projects.create(finalPayload);

        if (!response.success) {
          const msg = response.message || '创建项目失败';
          if (/配额|额度|未开通|套餐/.test(msg)) {
            onClose();
            window.dispatchEvent(
              new CustomEvent('showPaymentDialog', {
                detail: {
                  reason: 'quota_exceeded',
                  message: msg,
                },
              }),
            );
            return;
          } else {
            throw new Error(msg);
          }
        } else {
          showSuccessMessage('项目创建成功！');
        }
      }

      onClose();
      onProjectCreated();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 根据项目类型获取对话框标题
  const getDialogTitle = () => {
    if (editMode) {
      return '编辑项目';
    }
    switch (projectType) {
      case 'project_self_evaluation':
        return '创建项目自评';
      case 'department_self_evaluation':
        return '创建部门自评';
      case 'department_monitoring':
        return '创建部门监控';
      case 'monitoring_target':
      default:
        return '创建目标监控';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        {checkingQuota && !editMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            正在检查项目配额，请稍候...
          </Alert>
        )}
        {quotaError && !canCreateProject && !editMode && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {quotaError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="项目名称 *"
          fullWidth
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
        />
        {!hideExtraFields && (
          <>
            <TextField
              margin="dense"
              label="年度 *"
              fullWidth
              value={contractYear}
              onChange={(e) => setContractYear(e.target.value)}
              helperText="请输入项目所属年度，如 2025"
            />
            <TextField
              margin="dense"
              label="部门名称 *"
              fullWidth
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="单位名称 *"
              fullWidth
              value={unitName}
              onChange={(e) => setUnitName(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting || (!canCreateProject && !editMode)}
          startIcon={submitting ? <CircularProgress size={18} /> : undefined}
        >
          {editMode ? '保存' : '创建'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


